import { useEffect, useState } from 'react';
import {
  Autocomplete, Box, Button, CircularProgress, Divider, Grid, TextField, Typography
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { ClientInfo, ShippingInfo } from '../../Utils/types';
import { getData } from '../../Services/getData';
import { UrlEnum, validateEmail, validatePhone, validatePickupDate } from '../../Utils/Utils';
import { Vocabulary } from '../../Utils/Vocabulary';

interface Props {
  onSubmit: (data: { clientInfo: ClientInfo; shipping: ShippingInfo }) => void;
  onBack: () => void;
  loading: boolean;
}

interface Errors {
  name?: string; email?: string; phoneNumber?: string;
  pickupDate?: string; county?: string; city?: string;
  street?: string; number?: string; zipCode?: string;
}

export default function StepDelivery({ onSubmit, onBack, loading }: Props) {
  const [clientInfo, setClientInfo] = useState<ClientInfo>({ name: '', email: '', phoneNumber: '' });
  const [shipping, setShipping] = useState<ShippingInfo>({
    pickupDate: moment().add(1, 'day').format('YYYY-MM-DDTHH:mm:ss'),
    county: '', city: '', street: '', number: '', zipCode: '', details: '',
  });
  const [errors, setErrors] = useState<Errors>({});
  const [localities, setLocalities] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [streets, setStreets] = useState<string[]>([]);

  useEffect(() => {
    getData(UrlEnum.localities).then((res) => { if (res?.data) setLocalities(res.data); });
  }, []);

  useEffect(() => {
    if (shipping.county) {
      getData(`${UrlEnum.cities}/${shipping.county}`).then((res) => { if (res?.data) setCities(res.data); });
    } else {
      setCities([]);
    }
  }, [shipping.county]);

  useEffect(() => {
    if (shipping.county && shipping.city) {
      getData(`${UrlEnum.streets}/${shipping.county}/${shipping.city}`).then((res) => { if (res?.data) setStreets(res.data); });
    } else {
      setStreets([]);
    }
  }, [shipping.county, shipping.city]);

  function validate(): Errors {
    const e: Errors = {};
    if (!clientInfo.name.trim() || clientInfo.name.trim().length < 2) e.name = Vocabulary.requiredField;
    if (!clientInfo.email.trim()) e.email = Vocabulary.requiredField;
    else if (!validateEmail(clientInfo.email)) e.email = Vocabulary.invalidEmail;
    if (!clientInfo.phoneNumber.trim()) e.phoneNumber = Vocabulary.requiredField;
    else if (!validatePhone(clientInfo.phoneNumber)) e.phoneNumber = Vocabulary.invalidPhone;
    if (!shipping.pickupDate) e.pickupDate = Vocabulary.requiredField;
    else if (!validatePickupDate(shipping.pickupDate)) e.pickupDate = Vocabulary.pickupDatePast;
    if (!shipping.county) e.county = Vocabulary.requiredField;
    if (!shipping.city) e.city = Vocabulary.requiredField;
    if (!shipping.street) e.street = Vocabulary.requiredField;
    if (!shipping.number.trim()) e.number = Vocabulary.requiredField;
    if (!shipping.zipCode.trim()) e.zipCode = Vocabulary.requiredField;
    return e;
  }

  function handleSubmit() {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setErrors({});
    onSubmit({ clientInfo, shipping });
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>{Vocabulary.stepDelivery}</Typography>

      <Typography variant="h6" mt={2} mb={1}>{Vocabulary.clientInfo}</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label={Vocabulary.clientName} value={clientInfo.name}
            onChange={(e) => setClientInfo({ ...clientInfo, name: e.target.value })}
            error={!!errors.name} helperText={errors.name} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label={Vocabulary.clientEmail} value={clientInfo.email}
            onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
            error={!!errors.email} helperText={errors.email} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label={Vocabulary.clientPhone} value={clientInfo.phoneNumber}
            onChange={(e) => setClientInfo({ ...clientInfo, phoneNumber: e.target.value })}
            error={!!errors.phoneNumber} helperText={errors.phoneNumber} />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" mb={1}>{Vocabulary.shipping}</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker label={Vocabulary.pickupDate} inputFormat="DD-MM-YYYY"
              value={moment(shipping.pickupDate)}
              onChange={(v: any) => setShipping({ ...shipping, pickupDate: v?.format('YYYY-MM-DDTHH:mm:ss') || '' })}
              renderInput={(params) => (
                <TextField {...params} fullWidth error={!!errors.pickupDate} helperText={errors.pickupDate} />
              )} />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete options={localities} value={shipping.county}
            onChange={(_, v) => setShipping({ ...shipping, county: v || '', city: '', street: '' })}
            renderInput={(params) => (
              <TextField {...params} label={Vocabulary.county} error={!!errors.county} helperText={errors.county} />
            )} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete options={cities} value={shipping.city}
            onChange={(_, v) => setShipping({ ...shipping, city: v || '', street: '' })}
            renderInput={(params) => (
              <TextField {...params} label={Vocabulary.city} error={!!errors.city} helperText={errors.city} />
            )} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete options={streets} value={shipping.street}
            onChange={(_, v) => setShipping({ ...shipping, street: v || '' })}
            renderInput={(params) => (
              <TextField {...params} label={Vocabulary.street} error={!!errors.street} helperText={errors.street} />
            )} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label={Vocabulary.streetNumber} value={shipping.number}
            onChange={(e) => setShipping({ ...shipping, number: e.target.value })}
            error={!!errors.number} helperText={errors.number} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label={Vocabulary.zipCode} value={shipping.zipCode}
            onChange={(e) => setShipping({ ...shipping, zipCode: e.target.value })}
            error={!!errors.zipCode} helperText={errors.zipCode} />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label={Vocabulary.details} value={shipping.details}
            onChange={(e) => setShipping({ ...shipping, details: e.target.value })} />
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="space-between" mt={4}>
        <Button variant="outlined" onClick={onBack} disabled={loading}>{Vocabulary.back}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={22} color="inherit" /> : Vocabulary.finalizeOrder}
        </Button>
      </Box>
    </Box>
  );
}
